import React, { createContext, useContext, useEffect } from 'react'
import { useAtom } from 'jotai'
import { useAuth } from './AuthContext'
import { 
  currentOrganizationAtom,
  currentMembershipAtom,
  setOrganizationSetupStepAtom
} from '../store/organizationAtoms'
import { memberService } from '../services/firestoreService'

interface OrganizationContextType {
  loadUserOrganization: () => Promise<void>
  refreshOrganization: () => Promise<void>
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

export const useOrganization = () => {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}

interface OrganizationProviderProps {
  children: React.ReactNode
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { currentUser } = useAuth()
  const [, setCurrentOrganization] = useAtom(currentOrganizationAtom)
  const [, setCurrentMembership] = useAtom(currentMembershipAtom)
  const [, setOrganizationSetupStep] = useAtom(setOrganizationSetupStepAtom)

  const loadUserOrganization = async () => {
    if (!currentUser) {
      setCurrentOrganization(null)
      setCurrentMembership(null)
      return
    }

    try {
      // Get user's organizations
      const userOrganizations = await memberService.getUserOrganizations(currentUser.uid)
      
      if (userOrganizations.length > 0) {
        // For now, use the first organization (in the future, we might support multiple)
        const { organization, membership } = userOrganizations[0]
        setCurrentOrganization(organization)
        setCurrentMembership(membership)
        setOrganizationSetupStep('complete')
      } else {
        // User has no organization
        setCurrentOrganization(null)
        setCurrentMembership(null)
        setOrganizationSetupStep('none')
      }
    } catch (error) {
      console.error('Error loading user organization:', error)
      setCurrentOrganization(null)
      setCurrentMembership(null)
    }
  }

  const refreshOrganization = async () => {
    await loadUserOrganization()
  }

  // Load organization when user changes
  useEffect(() => {
    loadUserOrganization()
  }, [currentUser])

  const value: OrganizationContextType = {
    loadUserOrganization,
    refreshOrganization
  }

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
} 